import { builtEnvironment, isRunningLocal } from '@shared';
import { ApiClient } from './apiClient';
import getBaseUrl from '@shared/baseUrl';
import { API } from '@/api/types';

const origin = getBaseUrl();
const env = isRunningLocal() ? 'dev' : builtEnvironment;
const waterlilyBaseURL = `${origin}/${env}/api`;

export const WaterlilyClient = new ApiClient({
  baseURL: waterlilyBaseURL,
  onUnauthorized: () => {
    WaterlilyClient.clearToken();
  },
});

function createQueryKeys<T extends string>(resource: T) {
  return {
    all: [resource] as const,
    lists: () => [resource, 'list'] as const,
    list: <P extends Record<string, unknown>>(params: P) => [resource, 'list', { params }] as const,
    details: () => [resource, 'detail'] as const,
    detail: <K extends string>(id: K) => [resource, 'detail', id] as const,
  };
}

export const clientKeys = createQueryKeys('client');

export const clientsApi = {
  list: (params: { page: number }) => WaterlilyClient.get<API.ClientList>('/clients', params),
  getById: (id: string) => WaterlilyClient.get<API.Client>(`/client/${id}`),
  updateById: (id: string, data: Partial<API.Client>) =>
    WaterlilyClient.put<API.Client>(`/client/${id}`, data),
  deleteById: (id: string) => WaterlilyClient.delete<{ success: boolean }>(`/client/${id}`),
  getCalculations: (params: { page: number }) =>
    WaterlilyClient.get<API.Client>(`/client/calculations`),
};

export const advisorKeys = createQueryKeys('advisor');

export const advisorsApi = {
  list: (params: { page: number }) => WaterlilyClient.get<API.AdvisorList>('/advisors', params),
  getById: (id: string) => WaterlilyClient.get<API.Advisor>(`/advisor/${id}`),
};

export const inviteKeys = createQueryKeys('invite');

export const inviteApi = {
  sendInvite: (data: IntakeFormEmailProps) =>
    WaterlilyClient.post<{ success: boolean; result?: unknown; error?: string }>(
      '/intake-form-email',
      data,
    ),
  sendBatchInvite: (data: BatchIntakeFormEmailProps) =>
    WaterlilyClient.post<BatchEmailReminderResult>('/batch-intake-form-email', data),
};

export interface QuickClientWithFormRequest {
  advisorId: string;
  firstName: string;
  lastName: string;
  clientRelationshipStage?: ClientRelationshipStage;
}

export interface QuickClientWithFormResponse {
  clientId: string;
  formReferenceId: string;
}

export const quickClientApi = {
  createQuickClientWithForm: (data: QuickClientWithFormRequest) =>
    WaterlilyClient.post<QuickClientWithFormResponse>(
      `/advisor/${data.advisorId}/quick-client-with-form`,
      data,
    ),
};
